/**
 * Shared unread / mention badge.
 *
 * **Both modes show a number**, because the number is what the badge is for: it
 * answers "open this now, or later?" — one message and forty look identical as a
 * dot, and they are not the same decision. The mobile client has always shown
 * counts here; the web used to show a dot for plain unread, and the two clients
 * disagreeing about the same list was the actual bug.
 *
 * What still differs is loudness, not information:
 *
 *   - **unread → accent pill.** Ambient. A column of these reads as "activity",
 *     and the eye can skip it.
 *   - **mention → danger pill, prefixed `@`.** Someone addressed you. Escapes any
 *     visual budget, and stays capped at 9+ because past that the exact number
 *     changes nothing about what you do next.
 *
 * Plain unread caps at 99+, matching the mobile badge, so the same conversation
 * never reads differently on phone and desktop.
 *
 * Variants:
 *   floating — absolute-positioned on a tile, with a 2px ring matching the
 *     surrounding chrome bg so the badge reads as "stuck on top".
 *   inline (default) — flow-positioned at the end of a list row (DmRow /
 *     ChannelRow).
 */
import { useTranslation } from 'react-i18next';

/** Shared pill geometry: identical in both modes so a row never changes height
 *  when a mention arrives. */
const PILL =
  'grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full px-1.5 font-mono text-[10px] font-light tabular-nums';

export function UnreadBadge(props: {
  unread: number;
  mentions: number;
  floating?: boolean;
  /** Hide the "@" prefix on mentions — TopBar prefers a clean number
   *  when the tile already has a separate mute-state @ glyph nearby. */
  hideMentionAt?: boolean;
  className?: string;
}) {
  const { unread, mentions, floating, hideMentionAt, className } = props;
  const { t } = useTranslation();
  if (unread <= 0 && mentions <= 0) return null;
  const isMention = mentions > 0;

  const label = isMention
    ? (hideMentionAt ? '' : '@') + (mentions > 9 ? '9+' : mentions)
    : unread > 99
      ? '99+'
      : String(unread);

  return (
    <span
      className={
        PILL +
        (isMention ? ' bg-danger text-bg' : ' bg-accent text-bg') +
        (floating ? ' pointer-events-none absolute -right-0.5 -top-0.5 ring-2 ring-bg-subtle' : '') +
        (className ? ' ' + className : '')
      }
      aria-label={
        isMention ? undefined : t('ui.unreadBadge', { n: unread > 99 ? '99+' : unread })
      }
    >
      {label}
    </span>
  );
}
